import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { Auth0Provider } from "./utils/react-auth0-wrapper";
import { E2eStubAuth0Provider, e2eAuthStubEnabled } from "./utils/e2eAuthStub";
import { AuthApolloProvider } from "./utils/AuthApolloProvider";
import App from "./App";
import configureStore from "./store/configureStore";

//import env from "@beam-australia/react-env";
import env from "@icon-savings/react-env-heroku";
import { adminSite, isProduction } from "utils/determineSite";
import Bluebird from "bluebird";

// Bluebird replaces the global Promise so we get its cancellation feature; the
// cast bridges Bluebird's type onto the global Promise type.
window.Promise = Bluebird as unknown as typeof window.Promise;
// enable Promise cancellation bluebird feature. The default import's interop
// type drops the static `config`, so cast to reach it; the global Promise is
// now Bluebird (set above), so this configures the same object at runtime.
(
  Promise as unknown as {
    config: (options: {
      cancellation?: boolean;
      warnings?: boolean | { wForgottenReturn: boolean };
    }) => void;
  }
).config({
  cancellation: true,
  warnings: {
    wForgottenReturn: false, // there are many places where we create a promise without necessarily meaning to return anything
  },
});

// polyfill to support react-pdf. `withResolvers` is not in the ES2020 lib that
// types the global Promise, so cast the constructor to attach it.
type WithResolvers = {
  withResolvers: () => {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    promise: Promise<unknown>;
  };
};
(window.Promise as unknown as WithResolvers).withResolvers = function () {
  let resolve!: (value: unknown) => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { resolve, reject, promise };
};

const store = configureStore();

Sentry.init({
  dsn: env("SENTRY_DSN"),
  // @ts-expect-error isProduction is a function and is intentionally left
  // uncalled here to preserve byte-identical runtime behavior during the JS→TS
  // conversion (it is always truthy, so this resolves to "production").
  environment: isProduction ? "production" : "staging",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
    }),
  ],

  tracesSampleRate: 1.0,
  normalizeDepth: 10,
});

// onRedirectCallback only cleans the URL. Post-login navigation is handled by
// App.js's useEffect reading sessionStorage — doing it here races the router's
// internal state and lands users on /login → /dashboard instead of the target.
const onRedirectCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

if (adminSite()) {
  document.body.classList.add("icon-admin");
}

const container = document.getElementById("root");
const root = createRoot(container!);

// Playwright e2e runs (REACT_APP_E2E_AUTH_STUB=true, see .env.e2e) swap the
// real Auth0 provider for a fake always-authenticated context so tests skip
// the Auth0 redirect dance while MSW mocks the GraphQL layer.
const AuthProvider = e2eAuthStubEnabled()
  ? E2eStubAuth0Provider
  : Auth0Provider;

root.render(
  <AuthProvider
    domain={env("AUTH0_DOMAIN")}
    clientId={env("AUTH0_CLIENTID")}
    audience={`https://${env("AUTH0_AUDIENCE")}`}
    redirect_uri={window.location.origin}
    onRedirectCallback={onRedirectCallback}
    useRefreshTokens={true}
  >
    <Provider store={store}>
      <BrowserRouter>
        <AuthApolloProvider>
          <App />
        </AuthApolloProvider>
      </BrowserRouter>
    </Provider>
  </AuthProvider>
);
